import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Container,
  Divider,
  Tooltip,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Stack,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChatIcon from '@mui/icons-material/Chat';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SchoolIcon from '@mui/icons-material/School';
import LanguageIcon from '@mui/icons-material/Language';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';
import SourceIcon from '@mui/icons-material/Source';
import CodeIcon from '@mui/icons-material/Code';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BuildIcon from '@mui/icons-material/Build';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ContactModal from '../components/ContactModal';
import { getContactInfo } from '../utils/iframe';

const HomePage = () => {
  const navigate = useNavigate();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const contactInfo = getContactInfo();

  // 모달 핸들러
  const handleContactModalOpen = () => {
    setContactModalOpen(true);
  };

  const handleContactModalClose = () => {
    setContactModalOpen(false);
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      padding: 0,
      margin: 0,
      boxSizing: 'border-box',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* 히어로 섹션 - 고급스럽고 전문적인 디자인 */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 60%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)
          `,
          animation: 'pulse 4s ease-in-out infinite'
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            textAlign: 'center',
            color: 'white'
          }}>
            <Box sx={{ mb: 6 }}>
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
                  mb: 3,
                  background: 'linear-gradient(45deg, #ffffff 30%, #e2e8f0 70%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                  textShadow: '0 0 30px rgba(255,255,255,0.2)'
                }}
              >
                빅스데이터
              </Typography>

              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 500,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  mb: 4,
                  opacity: 0.9,
                  color: '#e2e8f0',
                  letterSpacing: '-0.01em'
                }}
              >
                AI 기반 질의응답 시스템
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.3rem' },
                  mb: 6,
                  opacity: 0.8,
                  color: '#cbd5e1',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                최신의 AI 기술로 구현된 지능형 질의응답 시스템을 통해
                <br />
                더 스마트한 정보 검색과 분석을 경험하세요
              </Typography>
              
              <Button 
                variant="contained" 
                size="large" 
                startIcon={<ChatIcon sx={{ fontSize: '1.5rem' }} />}
                onClick={() => navigate('/chat')}
                sx={{ 
                  py: 3,
                  px: 8,
                  fontSize: { xs: '1.2rem', sm: '1.4rem' },
                  fontWeight: 600,
                  borderRadius: '16px',
                  background: 'linear-gradient(45deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                  color: 'white',
                  boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  textTransform: 'none',
                  letterSpacing: '0.02em',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #2563eb 0%, #1e40af 50%, #1e3a8a 100%)',
                    boxShadow: '0 25px 50px rgba(59, 130, 246, 0.4), 0 0 0 1px rgba(255,255,255,0.2)',
                    transform: 'translateY(-3px) scale(1.02)',
                  },
                  '&:active': {
                    transform: 'translateY(-1px) scale(0.98)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                AI 질의응답 시작하기
              </Button>
            </Box>
          </Box>
        </Container>
        
        {/* 장식적 요소들 */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          animation: 'float 6s ease-in-out infinite'
        }} />
        
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
          animation: 'float 8s ease-in-out infinite reverse'
        }} />
        
        <Box sx={{
          position: 'absolute',
          bottom: '20%',
          left: '20%',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
          animation: 'float 7s ease-in-out infinite'
        }} />
      </Box>

      {/* 도입 상담 문의 모달 */}
      <ContactModal 
        open={contactModalOpen} 
        onClose={handleContactModalClose}
        contactInfo={contactInfo}
      />
      
      {/* CSS 애니메이션 */}
      <style>
        {`
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) rotate(0deg); 
              opacity: 0.7;
            }
            50% { 
              transform: translateY(-20px) rotate(180deg); 
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0%, 100% { 
              opacity: 0.5;
            }
            50% { 
              opacity: 0.8;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default HomePage; 